import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { CHAT_MAX_TEXT, isChatOpen, LOT_STATUS_LABELS, type ChatThread, type LotChat as LotChatData, type LotStatus } from "@omts/shared";
import { api, subscribeEvents } from "../api";
import { fmtDateTime, fmtTime } from "../time";

const errMsg = (e: unknown) => (e instanceof Error ? e.message : "Неизвестная ошибка");

/**
 * Блок «Чат с перевозчиками» в карточке лота (раздел 15.2): вкладки по компаниям с бейджем непрочитанных,
 * лента сообщений, поле ввода, «Начать переписку» с любым получателем. Обновляется по SSE; при открытии
 * вкладки сообщения перевозчика помечаются прочитанными.
 */
export function LotChat({ lotId, lotStatus }: { lotId: string; lotStatus: LotStatus }) {
  const [data, setData] = useState<LotChatData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<string | null>(null);
  /** Новый тред, открытый через «Начать переписку» (ещё без сообщений). */
  const [draftCompany, setDraftCompany] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [pick, setPick] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const logRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    api
      .lotChat(lotId)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => setError(errMsg(e)));
  }, [lotId]);

  useEffect(() => {
    load();
    return subscribeEvents((type, payload) => {
      if ((type === "chat.message" || type === "chat.read") && (payload as { lotId?: string }).lotId === lotId) load();
    });
  }, [load, lotId]);

  // Активная вкладка: первая (с последним сообщением), пока логист не выбрал сам.
  const threads = data?.threads ?? [];
  const activeId = active ?? draftCompany ?? threads[0]?.companyId ?? null;
  const thread: ChatThread | null = threads.find((t) => t.companyId === activeId) ?? null;
  const draft = !thread && draftCompany ? data?.companies.find((c) => c.companyId === draftCompany) : null;
  const open = data ? data.open : isChatOpen(lotStatus);
  const totalUnread = threads.reduce((s, t) => s + t.unreadCount, 0);

  // Открыли вкладку с непрочитанными — отмечаем прочитанными (сервер пришлёт chat.read → перечитаем).
  useEffect(() => {
    if (thread && thread.unreadCount > 0) void api.readChat(lotId, thread.companyId).catch(() => undefined);
  }, [lotId, thread?.companyId, thread?.unreadCount, thread]);

  // Прокрутка ленты вниз при смене вкладки и новых сообщениях.
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread?.companyId, thread?.messages.length]);

  const companiesToStart = (data?.companies ?? []).filter((c) => !threads.some((t) => t.companyId === c.companyId));

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    const companyId = thread?.companyId ?? draftCompany;
    const t = text.trim();
    if (!companyId || !t) return;
    setBusy(true);
    setSendError(null);
    try {
      const res = await api.sendChat(lotId, companyId, t);
      setText("");
      setDraftCompany(null);
      setActive(companyId);
      if (!res.delivered) setSendError("Сообщение сохранено, но в Telegram не доставлено — перевозчик не увидит его в боте.");
      load();
    } catch (err) {
      setSendError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const current = thread ? { companyId: thread.companyId, companyName: thread.companyName, telegramLinked: thread.telegramLinked } : draft ?? null;

  return (
    <section className="card chat">
      <div className="row between">
        <h3>
          Чат с перевозчиками
          {totalUnread > 0 && <span className="badge danger">{totalUnread} новых</span>}
        </h3>
        {open && companiesToStart.length > 0 && !starting && (
          <button type="button" className="btn small" onClick={() => setStarting(true)}>
            Начать переписку
          </button>
        )}
      </div>

      {error && <p className="error">{error}</p>}

      {starting && (
        <form
          className="chat-start"
          onSubmit={(e) => {
            e.preventDefault();
            if (!pick) return;
            setDraftCompany(pick);
            setActive(null);
            setStarting(false);
            setPick("");
          }}
        >
          <select value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">— выберите перевозчика —</option>
            {companiesToStart.map((c) => (
              <option key={c.companyId} value={c.companyId} disabled={!c.telegramLinked}>
                {c.companyName}
                {c.telegramLinked ? "" : " (Telegram не подключён)"}
              </option>
            ))}
          </select>
          <button type="submit" className="btn small primary" disabled={!pick}>
            Открыть
          </button>
          <button type="button" className="btn small" onClick={() => setStarting(false)}>
            Отмена
          </button>
        </form>
      )}

      {threads.length === 0 && !draft ? (
        <p className="muted">
          {open
            ? "Переписки пока нет. Перевозчик может написать через кнопку «Написать логисту» в боте; вы — через «Начать переписку»."
            : "Переписки по этой заявке не было."}
        </p>
      ) : (
        <>
          <div className="chat-tabs" role="tablist">
            {threads.map((t) => (
              <button
                key={t.companyId}
                type="button"
                role="tab"
                aria-selected={t.companyId === activeId}
                className={t.companyId === activeId ? "active" : ""}
                onClick={() => {
                  setActive(t.companyId);
                  setDraftCompany(null);
                }}
              >
                {t.companyName}
                {t.unreadCount > 0 && <span className="count">{t.unreadCount}</span>}
              </button>
            ))}
            {draft && (
              <button type="button" role="tab" aria-selected className="active">
                {draft.companyName}
                <span className="tag">новая</span>
              </button>
            )}
          </div>

          <div className="chat-log" ref={logRef}>
            {thread?.messages.map((m) => (
              <div key={m.id} className={`chat-msg ${m.direction}${m.direction === "carrier" && !m.readAt ? " unread" : ""}`}>
                {m.text}
                <span className="meta" title={fmtDateTime(m.createdAt)}>
                  {m.direction === "logist" ? (m.authorName ?? "логист") : thread.companyName} · {fmtTime(m.createdAt)}
                  {m.direction === "logist" && m.telegramMessageId === null && <span className="warn"> · не доставлено в Telegram</span>}
                </span>
              </div>
            ))}
            {draft && <p className="muted" style={{ margin: 0 }}>Напишите первое сообщение — перевозчик получит его в Telegram и сможет ответить.</p>}
          </div>
        </>
      )}

      {current && open && (
        <form className="chat-form" onSubmit={(e) => void send(e)}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            maxLength={CHAT_MAX_TEXT}
            placeholder={current.telegramLinked ? `Сообщение для ${current.companyName}… (Enter — отправить, Shift+Enter — перенос)` : "У перевозчика не подключён Telegram"}
            disabled={busy || !current.telegramLinked}
            rows={2}
          />
          <button type="submit" className="btn primary" disabled={busy || !text.trim() || !current.telegramLinked}>
            {busy ? "…" : "Отправить"}
          </button>
        </form>
      )}
      {sendError && <p className="error">{sendError}</p>}
      {!open && (threads.length > 0 || draft) && (
        <p className="chat-closed">Чат закрыт: заявка в статусе «{LOT_STATUS_LABELS[lotStatus]}». Переписка сохранена.</p>
      )}
    </section>
  );
}
